/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
/* eslint-disable react-hooks/exhaustive-deps */
'use client';

import { SERVICE_REQUESTS } from '@ahm/api-wrappers-core';
import { useLocationStore } from '@ahm/react-map/store';
import {
  Button,
  Card,
  Collapse,
  rem,
  Stack,
  Title,
  type CardProps,
} from '@mantine/core';
import { useTranslations } from 'next-intl';
import {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import BookingNote from '../booking-note';
import SpecialRequestItem from './special-request-item';
import CaretRightIcon from '@/assets/icons/caret-right-icon.svg';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

type Props = Omit<CardProps, 'children'> & {
  // custom props here
};

export type ParamsSpecialRequestType = Record<
  string,
  string | number | boolean | undefined
>;
const COD_INSURANCE = 'COD_INSURANCE';

const SpecialRequests = memo(function SpecialRequests({ ...props }: Props) {
  const { selectedService, estimatedData, setServiceRequest, serviceRequests } =
    useBookingStore((state) => state);
  const paths = useLocationStore.use.paths();
  const isMobile = useIsMobile();
  const tBooking = useTranslations('booking');
  const specialRequestRef = useRef<HTMLDivElement>(null);

  const specialRequestList = useMemo(() => {
    if (!selectedService) return [];
    return selectedService?.requests || [];
  }, [selectedService]);

  // const initialPath = useMemo(() => {
  //   return paths;
  // }, [paths]);
  // const initialItemDescriptions = useMemo(() => {
  //   return paths
  //     .flatMap((path) =>
  //       (path.item_descriptions || []).map((item) => item.keyword)
  //     )
  //     .join(', ');
  // }, [paths]);

  const handleChangeRequestParams = useCallback(
    (id: string, value?: string | number | boolean | number[]) => {
      if (!id || !selectedService?._id) return;

      const param = { ...serviceRequests };
      if (value === undefined) {
        delete param[id as keyof typeof serviceRequests];
      } else {
        param[id as keyof typeof serviceRequests] = value;
      }

      setServiceRequest(param);
    },
    [serviceRequests, selectedService]
  );

  useEffect(() => {
    if (paths && paths?.length > 2) {
      const param = { ...serviceRequests };

      if (param?.[`${selectedService?._id}-SMS`]) {
        delete param[`${selectedService?._id}-SMS`];
      }
      if (param?.[`${selectedService?._id}-FRAGILE`] && estimatedData?.data) {
        const fragileRequest = specialRequestList.find(
          (request) => request._id === `${selectedService?._id}-FRAGILE`
        );
        if (
          fragileRequest &&
          Number(fragileRequest?.max_distance) <
            Number(estimatedData?.data?.distance) * 1000
        ) {
          delete param[`${selectedService?._id}-FRAGILE`];
        }
      }
      // if (param?.[`${selectedService?._id}-D2D`]) {
      //   delete param[`${selectedService?._id}-D2D`];
      // }
      setServiceRequest(param);
    }
  }, [paths]);

  // const mainRequestList = useMemo(() => {
  //   return specialRequestList.filter(
  //     (request) => request.type === COD_INSURANCE
  //   );
  // }, [specialRequestList]);

  const moreRequestList = useMemo(() => {
    const excludedTypes = [
      COD_INSURANCE,
      // SERVICE_REQUESTS.COMMISSION_DISTANCE_PERCENTAGE,
      SERVICE_REQUESTS.COMMISSION_FLAT,
      SERVICE_REQUESTS.COMMISSION_PERCENTAGE,
      SERVICE_REQUESTS.FRAGILE,
      SERVICE_REQUESTS.ITEM,
      SERVICE_REQUESTS.INSURANCE,
      SERVICE_REQUESTS.TIER,
    ];
    return specialRequestList.filter(
      (request) => !excludedTypes.includes(request.type)
    );
  }, [specialRequestList]);
  const [showMore, setShowMore] = useState(false);

  const handleShowMoreClick = () => {
    setShowMore((prev) => !prev);
    if (specialRequestRef.current) {
      specialRequestRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const renderListRequest = () => {
    if (moreRequestList.length === 0) {
      return null;
    }

    const defaultRequestShow = moreRequestList.slice(0, 3);
    const remainingRequestShow = moreRequestList.slice(3);
    const hasMoreToShow = remainingRequestShow.length > 0;

    return (
      <>
        <Stack gap={rem(4)} p={0}>
          {defaultRequestShow.map((request) => (
            <SpecialRequestItem
              key={request._id}
              request={request}
              service_requests={
                serviceRequests ? serviceRequests[request?._id] : undefined
              }
              handleChangeRequestParams={handleChangeRequestParams}
              isMobile={isMobile}
            />
          ))}
          <Collapse in={showMore}>
            <Stack gap={rem(4)} p={0}>
              {remainingRequestShow.map((request) => (
                <SpecialRequestItem
                  key={request._id}
                  request={request}
                  service_requests={
                    serviceRequests ? serviceRequests[request?._id] : undefined
                  }
                  handleChangeRequestParams={handleChangeRequestParams}
                  isMobile={isMobile}
                />
              ))}
            </Stack>
          </Collapse>
        </Stack>

        {hasMoreToShow && (
          <Button
            rightSection={
              <CaretRightIcon
                size={24}
                style={{
                  flex: 'none',
                  transform: showMore ? 'rotate(-90deg)' : 'rotate(90deg)',
                }}
              />
            }
            c="neutral.6"
            variant="subtle"
            styles={{
              root: {
                background: 'transparent',
              },
            }}
            onClick={handleShowMoreClick}
          >
            {showMore ? 'Thu gọn' : 'Xem thêm'}
          </Button>
        )}
      </>
    );
  };

  if (specialRequestList.length === 0 || !estimatedData || !selectedService)
    return <Fragment />;

  return (
    <div ref={specialRequestRef}>
      <Card
        radius={0}
        mx={-16}
        p={rem(16)}
        py={isMobile ? rem(12) : rem(16)}
        {...props}
        mt={0}
        styles={{
          root: {
            borderRadius: '16px 16px 0 0',
          },
        }}
      >
        <Title mb={rem(16)} order={isMobile ? 5 : 4}>
          {tBooking('special_requests.title')}
        </Title>

        <Stack gap={rem(12)}>
          <BookingNote />

          {renderListRequest()}
        </Stack>
      </Card>
    </div>
  );
});

export default SpecialRequests;
